// Super-admin-only toggle for the server-side response cache.
//
// Defaults:
//   localhost (hostname is "localhost" or "127.0.0.1") → cache OFF
//   anything else (any deployed env)                   → cache ON
//
// Once the super admin toggles, the explicit choice persists in localStorage
// and overrides the default. When effective state is OFF, every outbound
// request carries `x-bypass-cache: 1`, which the backend honors verbatim.

const SERVER_STORAGE_KEY = 'flow:server-cache-mode'
const LEGACY_SERVER_STORAGE_KEY = 'flow:server-cache-enabled'
const MODEL_STORAGE_KEY = 'flow:model-cache-mode'

function readStored(key: string): '1' | '0' | null {
	try {
		const v = localStorage.getItem(key)
		return v === '1' || v === '0' ? v : null
	} catch {
		return null
	}
}

function migrateLegacyServerKey(): void {
	try {
		const legacy = localStorage.getItem(LEGACY_SERVER_STORAGE_KEY)
		if (legacy === '0' || legacy === '1') {
			localStorage.setItem(SERVER_STORAGE_KEY, legacy === '0' ? '0' : '1')
			localStorage.removeItem(LEGACY_SERVER_STORAGE_KEY)
		}
	} catch {}
}

function isLocalhost(): boolean {
	try {
		const h = window.location.hostname
		return h === 'localhost' || h === '127.0.0.1'
	} catch {
		return false
	}
}

// Server response cache (Redis + in-memory on the API). Bypassed by sending `x-bypass-cache: 1`.
export function isServerCacheEnabled(): boolean {
	migrateLegacyServerKey()
	const stored = readStored(SERVER_STORAGE_KEY)
	if (stored === '1') return true
	if (stored === '0') return false
	return !isLocalhost()
}

export function setServerCacheEnabled(enabled: boolean): void {
	try {
		localStorage.setItem(SERVER_STORAGE_KEY, enabled ? '1' : '0')
		localStorage.removeItem(LEGACY_SERVER_STORAGE_KEY)
	} catch {}
}

export function getCacheBypassHeader(): { name: string; value: string } | null {
	return isServerCacheEnabled() ? null : { name: 'x-bypass-cache', value: '1' }
}

// Client-side in-memory model cache. Each of the 11 models has TWO independent caches
// (specs + metrics) and each can be toggled separately. Storage keys:
//   flow:model-cache-mode                            — global master, used as default
//   flow:model-cache-mode:<model>                    — per-model master (set when you toggle one
//                                                       model's row in the UI; flips both categories)
//   flow:model-cache-mode:<model>:<specs|metrics>    — finest grain, takes precedence
//
// Bypassed by `makeBypassableCache(modelKey, category)` returning `undefined` on every read
// when the resolved flag is off — forces the model code to fall through to client(...).

export type ModelCacheKey =
	| 'regions'
	| 'signals'
	| 'edge-groups'
	| 'systems'
	| 'intersections'
	| 'phases'
	| 'cerberus-map-metrics'
	| 'search'
	| 'cerberus-sidebar'
	| 'networks'
	| 'subregions'

export type ModelCacheCategory = 'specs' | 'metrics'

export const MODEL_CACHE_KEYS: ModelCacheKey[] = [
	'regions',
	'signals',
	'edge-groups',
	'systems',
	'intersections',
	'phases',
	'cerberus-map-metrics',
	'search',
	'cerberus-sidebar',
	'networks',
	'subregions',
]

export const MODEL_CACHE_CATEGORIES: ModelCacheCategory[] = ['specs', 'metrics']

const perModelKey = (model: ModelCacheKey) => `${MODEL_STORAGE_KEY}:${model}`
const perModelCategoryKey = (model: ModelCacheKey, category: ModelCacheCategory) => `${MODEL_STORAGE_KEY}:${model}:${category}`

// Read the effective state. Most specific wins:
//   (modelKey, category) → per-model-per-category → per-model → global master → default.
//   (modelKey)           → per-model              → global master → default.
//   ()                   → global master          → default.
//
// Default is ALWAYS on, including on localhost: the model cache holds rows
// fetched in the current session, and any flow that writes then reads (e.g.
// CerberusMapMetricsModel populating METRICS_CACHE for the map signal layer)
// relies on reads coming back. Bypassing it by default broke map coloring
// because the Proxy's `get` returns undefined for every property — including
// values just written milliseconds earlier. The server response cache, which
// does default off on localhost, is a separate concern.
export function isModelCacheEnabled(modelKey?: ModelCacheKey, category?: ModelCacheCategory): boolean {
	if (modelKey && category) {
		const v = readStored(perModelCategoryKey(modelKey, category))
		if (v === '1') return true
		if (v === '0') return false
	}
	if (modelKey) {
		const v = readStored(perModelKey(modelKey))
		if (v === '1') return true
		if (v === '0') return false
	}
	const master = readStored(MODEL_STORAGE_KEY)
	if (master === '1') return true
	if (master === '0') return false
	return true
}

// Write a toggle. Setting at a coarser grain propagates to all finer-grain keys beneath it
// (so the user sees consistent state without having to toggle 22 individual switches).
//   ()                         → global master + every per-model + every per-model-per-category.
//   (modelKey)                 → per-model master + both per-model-per-category for that model.
//   (modelKey, category)       → writes only the one per-model-per-category key.
//   (undefined, category)      → category-wide master: writes that category for every model.
export function setModelCacheEnabled(enabled: boolean, modelKey?: ModelCacheKey, category?: ModelCacheCategory): void {
	const v = enabled ? '1' : '0'
	try {
		if (modelKey && category) {
			localStorage.setItem(perModelCategoryKey(modelKey, category), v)
			return
		}
		if (modelKey) {
			localStorage.setItem(perModelKey(modelKey), v)
			for (const c of MODEL_CACHE_CATEGORIES) {
				localStorage.setItem(perModelCategoryKey(modelKey, c), v)
			}
			return
		}
		if (category) {
			// Category-wide master: flip this category for every model.
			for (const m of MODEL_CACHE_KEYS) {
				localStorage.setItem(perModelCategoryKey(m, category), v)
			}
			return
		}
		localStorage.setItem(MODEL_STORAGE_KEY, v)
		for (const m of MODEL_CACHE_KEYS) {
			localStorage.setItem(perModelKey(m), v)
			for (const c of MODEL_CACHE_CATEGORIES) {
				localStorage.setItem(perModelCategoryKey(m, c), v)
			}
		}
	} catch {}
}
